import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import apiClient from "@/services/api/client";
import { API_ENDPOINTS } from "@/services/api/endpoints";
import { QUERY_KEYS } from "@/services/api/queryKeys";
import { MUTATION_KEYS } from "@/services/api/mutationKeys";
import type {
  Portal,
  PortalsResponse,
  CreatePortalPayload,
  UpdatePortalPayload,
} from "@/types";

export const usePortals = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const portalsQuery = useQuery({
    queryKey: QUERY_KEYS.PORTALS.LIST(),
    queryFn: async () => {
      const response = await apiClient.get<PortalsResponse>(
        API_ENDPOINTS.SYSTEM.PORTALS.BASE
      );
      return response.data.portals;
    },
  });

  const createPortalMutation = useMutation({
    mutationKey: [MUTATION_KEYS.CREATE_PORTAL],
    mutationFn: async (payload: CreatePortalPayload) => {
      const response = await apiClient.post<Portal>(
        API_ENDPOINTS.SYSTEM.PORTALS.BASE,
        payload
      );
      return response.data;
    },
    onSuccess: (createdPortal) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTALS.ALL });
      navigate(`/dashboard/portals/${createdPortal.id}`, {
        state: { portal: createdPortal },
      });
    },
  });

  const updatePortalMutation = useMutation({
    mutationKey: [MUTATION_KEYS.UPDATE_PORTAL],
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdatePortalPayload;
    }) => {
      const response = await apiClient.patch<Portal>(
        API_ENDPOINTS.SYSTEM.PORTALS.DETAIL(id),
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTALS.ALL });
    },
  });

  const deletePortalMutation = useMutation({
    mutationKey: [MUTATION_KEYS.DELETE_PORTAL],
    mutationFn: async (id: string) => {
      await apiClient.delete(API_ENDPOINTS.SYSTEM.PORTALS.DETAIL(id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTALS.ALL });
    },
  });

  return {
    portals: portalsQuery.data ?? [],
    isLoading: portalsQuery.isLoading,
    error: portalsQuery.error,
    createPortal: createPortalMutation.mutate,
    createPortalAsync: createPortalMutation.mutateAsync,
    isCreating: createPortalMutation.isPending,
    updatePortal: updatePortalMutation.mutate,
    isUpdating: updatePortalMutation.isPending,
    deletePortal: deletePortalMutation.mutate,
    isDeleting: deletePortalMutation.isPending,
  };
};
